import { ReactElement, useCallback } from 'react';
import Link from 'next/link';
import * as Sentry from '@sentry/node';
import { dataLayerPush } from '@r1-frontend/ui-react/components/helpers';
import { ButtonWrap, ButtonStyle, ButtonText } from './styled';
import { TAnalytics } from '~/src/services/blocks/configs/Part/analyticsPartConfig';
import { btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';

export interface IButtonProps {
    elemId: string,
    type?: btnTypes,
    buttonText?: string | React.ReactNode | ReactElement;
    href?: string,
    isTargetBlank?: boolean,
    idForScroll?: string,
    analytics?: TAnalytics,
    customStyles?: Record<string, any>,
    buttonClickHandler?: () => void,
    className?: string,
    isInline?: boolean;
    backgroundColor?: string;
    backgroundHoverColor?: string;
    align?: 'left' | 'right' | 'center';
    isAutoWidth?: boolean;
    enableCalltouch?: boolean;
    propName?: string;
}

const Button = ({
    elemId,
    type,
    buttonText,
    href,
    isTargetBlank,
    idForScroll,
    customStyles,
    analytics,
    buttonClickHandler,
    className,
    isInline = false,
    backgroundColor,
    backgroundHoverColor,
    align = 'left',
    isAutoWidth = false,
    enableCalltouch = false,
    propName,
}: IButtonProps): JSX.Element => {
    const clickHandler = useCallback(() => {
        if (idForScroll) {
            try {
                const elem = document.querySelector(`#${idForScroll}`);
                elem?.scrollIntoView({ block: 'start', behavior: 'smooth' });
            } catch (err) {
                Sentry.captureException(err);
            }
        }
    }, [idForScroll]);

    const onSendGtmEvent = useCallback(() => {
        if (analytics) {
            const { category = '', action = '', label = '' } = analytics;

            if (category || action || label) {
                const event = {
                    event: 'UAevent',
                    ...analytics,
                    category: category || '',
                    action: action || '',
                };

                dataLayerPush(event);
            }
        }
    }, [analytics]);

    return (
        <ButtonWrap onClick={onSendGtmEvent} className={className} inline={isInline} align={align}>
            {href
                ?   <Link href={href} passHref>
                        <ButtonStyle
                            id={elemId}
                            type={type || btnTypes.ACCENT}
                            target={isTargetBlank ? '_blank' : undefined}
                            rel='noopener noreferrer nofollow'
                            data-testid='link-btn'
                            backgroundColor={backgroundColor}
                            backgroundHoverColor={backgroundHoverColor}
                            propName={propName}
                            isAutoWidth={isAutoWidth}
                            className={enableCalltouch ? 'js-calltouch-phone' : ''}
                            {...customStyles}
                        >
                            <ButtonText {...customStyles} propName={propName}>{buttonText || 'Купить'}</ButtonText>
                        </ButtonStyle>
                    </Link>
                :   <ButtonStyle
                        type={type || btnTypes.ACCENT}
                        onClick={buttonClickHandler || clickHandler}
                        backgroundColor={backgroundColor}
                        backgroundHoverColor={backgroundHoverColor}
                        propName={propName}
                        isAutoWidth={isAutoWidth}
                        {...customStyles}
                    >
                        <ButtonText {...customStyles} propName={propName}>{buttonText || 'Купить'}</ButtonText>
                    </ButtonStyle>
            }
        </ButtonWrap>
    );
};

export default Button;
